import React, { useState, useEffect } from 'react';
import { 
  Save, Plus, Trash2, Image as ImageIcon, GripVertical, 
  Globe, Truck, Phone, Mail, Instagram, Facebook, Twitter,
  AlertCircle, CheckCircle, Edit, X, Eye, EyeOff
} from 'lucide-react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/images';

const SettingsPanel = () => {
  const [activeTab, setActiveTab] = useState('banners');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data.success) {
        setSettings(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const res = await api.put('/settings/admin', settings);
      if (res.data.success) {
        setSettings(res.data.data);
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const tabs = [
    { id: 'banners', label: 'Banners & Hero' },
    { id: 'general', label: 'General' },
    { id: 'shipping', label: 'Shipping' },
    { id: 'contact', label: 'Contact & Social' }
  ];

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center py-20">
          <div className="text-2xl font-black animate-pulse">LOADING_SETTINGS...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-5xl font-black tracking-tighter mb-2">SETTINGS</h1>
          <div className="inline-block bg-yellow-400 text-black px-4 py-1 font-mono font-bold text-xs border-2 border-black">
            SITE_CONFIGURATION
          </div>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 bg-green-500 text-white px-6 py-3 font-black border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
        >
          <Save size={20} />
          {saving ? 'SAVING...' : 'SAVE ALL'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 border-4 ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-500 text-green-700' 
            : 'bg-red-50 border-red-500 text-red-700'
        } font-bold flex items-center gap-2`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b-4 border-black pb-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 font-black border-4 border-b-0 transition-all ${
              activeTab === tab.id
                ? 'bg-white border-black -mb-1 z-10'
                : 'bg-gray-200 border-gray-400 hover:bg-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white border-4 border-black p-6">
        {activeTab === 'banners' && <BannersTab settings={settings} setSettings={setSettings} />}
        {activeTab === 'general' && <GeneralTab settings={settings} setSettings={setSettings} />}
        {activeTab === 'shipping' && <ShippingTab settings={settings} setSettings={setSettings} />}
        {activeTab === 'contact' && <ContactTab settings={settings} setSettings={setSettings} />}
      </div>
    </div>
  );
};

// Banner Management Tab
const BannersTab = ({ settings, setSettings }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const deleteBanner = async (id) => {
    if (!confirm('Delete this banner?')) return;
    
    try {
      await api.delete(`/settings/admin/banners/${id}`);
      setSettings({
        ...settings,
        banners: settings.banners.filter(b => b._id !== id)
      });
    } catch (error) {
      alert('Error deleting banner');
    }
  };

  const toggleBanner = async (banner) => {
    try {
      await api.put(`/settings/admin/banners/${banner._id}`, {
        isActive: !banner.isActive
      });
      const updatedBanners = settings.banners.map(b =>
        b._id === banner._id ? { ...b, isActive: !b.isActive } : b
      );
      setSettings({ ...settings, banners: updatedBanners });
    } catch (error) {
      alert('Error updating banner');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-black">HOMEPAGE BANNERS</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-yellow-400 text-black px-4 py-2 font-bold border-2 border-black"
        >
          <Plus size={20} />
          ADD BANNER
        </button>
      </div>

      {settings.banners?.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300">
          <ImageIcon size={48} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-bold">No banners added yet</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 bg-yellow-400 px-6 py-2 font-bold border-2 border-black"
          >
            Add Your First Banner
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {settings.banners.map((banner) => (
            <div
              key={banner._id}
              className={`flex gap-4 p-4 border-4 border-black ${
                !banner.isActive ? 'opacity-50' : ''
              }`}
            >
              <div className="w-32 h-20 bg-gray-100 border-2 border-black flex items-center justify-center flex-shrink-0">
                {banner.image?.url ? (
                  <img
                    src={resolveImageUrl(banner.image.url)}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={32} className="text-gray-400" />
                )}
              </div>
              
              <div className="flex-grow">
                <h3 className="font-black text-lg">{banner.title || 'Untitled Banner'}</h3>
                <p className="text-sm text-gray-600">{banner.subtitle}</p>
                <div className="flex gap-2 mt-2">
                  <span className="text-xs bg-gray-100 px-2 py-1 border border-gray-300 uppercase font-bold">
                    {banner.category || 'HOME'}
                  </span>
                  {banner.isActive && (
                    <span className="text-xs bg-green-100 px-2 py-1 border border-green-300 text-green-700 font-bold">
                      ACTIVE
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => toggleBanner(banner)}
                  className={`p-2 border-2 border-black ${
                    banner.isActive ? 'bg-green-400' : 'bg-gray-200'
                  }`}
                  title={banner.isActive ? 'Deactivate' : 'Activate'}
                >
                  {banner.isActive ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
                <button
                  onClick={() => {
                    setEditingBanner(banner);
                    setShowAddModal(true);
                  }}
                  className="p-2 bg-blue-500 text-white border-2 border-black"
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => deleteBanner(banner._id)}
                  className="p-2 bg-red-500 text-white border-2 border-black"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <BannerModal
          banner={editingBanner}
          onClose={() => {
            setShowAddModal(false);
            setEditingBanner(null);
          }}
          onSuccess={(newBanners) => {
            setSettings({ ...settings, banners: newBanners });
            setShowAddModal(false);
            setEditingBanner(null);
          }}
        />
      )}
    </div>
  );
};

// Banner Modal
const BannerModal = ({ banner, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    link: banner?.link || '',
    buttonText: banner?.buttonText || '',
    category: banner?.category || 'home',
    isActive: banner?.isActive !== undefined ? banner.isActive : true
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });
      if (imageFile) {
        data.append('image', imageFile);
      }

      let res;
      if (banner) {
        res = await api.put(`/settings/admin/banners/${banner._id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post('/settings/admin/banners', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (res.data.success) {
        onSuccess(res.data.data);
      }
    } catch (error) {
      alert('Error: ' + error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border-4 border-yellow-400 w-full max-w-lg">
        <div className="bg-yellow-400 p-4 border-b-4 border-black flex justify-between items-center">
          <h2 className="text-2xl font-black">{banner ? 'EDIT BANNER' : 'ADD BANNER'}</h2>
          <button onClick={onClose} className="text-2xl font-black">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-black uppercase mb-2">Banner Title*</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold"
              placeholder="NEW COLLECTION"
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase mb-2">Subtitle</label>
            <input
              type="text"
              value={formData.subtitle}
              onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black"
              placeholder="Limited Edition Drop"
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase mb-2">Banner Image*</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              className="w-full px-4 py-3 border-2 border-black"
              required={!banner}
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 1920x600px</p>
          </div>

          <div>
            <label className="block text-xs font-black uppercase mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold bg-white"
            >
              <option value="home">HOME</option>
              <option value="anime">OTAKU</option>
              <option value="tantra">TANTRA</option>
              <option value="inkspeak">INKSPEAK</option>
              <option value="soul">SOUL</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-black uppercase mb-2">Link URL</label>
            <input
              type="text"
              value={formData.link}
              onChange={(e) => setFormData({...formData, link: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-mono"
              placeholder="/products/anime"
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase mb-2">Button Text</label>
            <input
              type="text"
              value={formData.buttonText}
              onChange={(e) => setFormData({...formData, buttonText: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold"
              placeholder="SHOP NOW"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="font-bold">Active (show on website)</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-yellow-400 text-black font-black text-lg uppercase py-4 border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
          >
            {loading ? 'SAVING...' : (banner ? 'UPDATE BANNER' : 'ADD BANNER')}
          </button>
        </form>
      </div>
    </div>
  );
};

// General Settings Tab
const GeneralTab = ({ settings, setSettings }) => {
  const update = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-black uppercase mb-2">Site Name</label>
        <input
          type="text"
          value={settings.siteName}
          onChange={(e) => update('siteName', e.target.value)}
          className="w-full px-4 py-3 border-2 border-black font-bold"
        />
      </div>

      <div>
        <label className="block text-sm font-black uppercase mb-2">Tagline</label>
        <input
          type="text"
          value={settings.tagline}
          onChange={(e) => update('tagline', e.target.value)}
          className="w-full px-4 py-3 border-2 border-black"
        />
      </div>

      <div className="border-4 border-black p-4 bg-gray-50">
        <h3 className="font-black mb-4">HOMEPAGE DISPLAY</h3>
        
        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.homepage?.showTrending}
            onChange={(e) => setSettings({
              ...settings,
              homepage: { ...settings.homepage, showTrending: e.target.checked }
            })}
            className="w-5 h-5"
          />
          <span className="font-bold">Show Trending Section</span>
        </label>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.homepage?.showNewArrivals}
            onChange={(e) => setSettings({
              ...settings,
              homepage: { ...settings.homepage, showNewArrivals: e.target.checked }
            })}
            className="w-5 h-5"
          />
          <span className="font-bold">Show New Arrivals</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.homepage?.showBestSellers}
            onChange={(e) => setSettings({
              ...settings,
              homepage: { ...settings.homepage, showBestSellers: e.target.checked }
            })}
            className="w-5 h-5"
          />
          <span className="font-bold">Show Best Sellers</span>
        </label>
      </div>
    </div>
  );
};

// Shipping Settings Tab
const ShippingTab = ({ settings, setSettings }) => {
  const updateShipping = (field, value) => {
    setSettings({
      ...settings,
      shipping: { ...settings.shipping, [field]: value }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-black uppercase mb-2">Free Shipping Threshold (₹)</label>
        <input
          type="number"
          value={settings.shipping?.freeShippingThreshold}
          onChange={(e) => updateShipping('freeShippingThreshold', e.target.value)}
          className="w-full px-4 py-3 border-2 border-black font-bold"
        />
        <p className="text-sm text-gray-600 mt-1">Orders above this amount get free shipping</p>
      </div>

      <div>
        <label className="block text-sm font-black uppercase mb-2">Standard Shipping Charge (₹)</label>
        <input
          type="number"
          value={settings.shipping?.standardCharge}
          onChange={(e) => updateShipping('standardCharge', e.target.value)}
          className="w-full px-4 py-3 border-2 border-black font-bold"
        />
      </div>

      <div>
        <label className="block text-sm font-black uppercase mb-2">COD Charge (₹)</label>
        <input
          type="number"
          value={settings.shipping?.codCharge}
          onChange={(e) => updateShipping('codCharge', e.target.value)}
          className="w-full px-4 py-3 border-2 border-black font-bold"
        />
        <p className="text-sm text-gray-600 mt-1">Extra charge for Cash on Delivery orders</p>
      </div>
    </div>
  );
};

// Contact & Social Tab
const ContactTab = ({ settings, setSettings }) => {
  const updateContact = (field, value) => {
    setSettings({
      ...settings,
      contact: { ...settings.contact, [field]: value }
    });
  };

  const updateSocial = (field, value) => {
    setSettings({
      ...settings,
      social: { ...settings.social, [field]: value }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-black text-xl mb-4 flex items-center gap-2">
          <Phone size={24} />
          CONTACT INFORMATION
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-black uppercase mb-2">Email</label>
            <input
              type="email"
              value={settings.contact?.email || ''}
              onChange={(e) => updateContact('email', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-black uppercase mb-2">Phone</label>
            <input
              type="text"
              value={settings.contact?.phone || ''}
              onChange={(e) => updateContact('phone', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-black uppercase mb-2">WhatsApp Number</label>
            <input
              type="text"
              value={settings.contact?.whatsapp || ''}
              onChange={(e) => updateContact('whatsapp', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-mono"
              placeholder="917054083677"
            />
          </div>

          <div>
            <label className="block text-sm font-black uppercase mb-2">Address</label>
            <textarea
              value={settings.contact?.address || ''}
              onChange={(e) => updateContact('address', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black"
              rows="3"
            />
          </div>
        </div>
      </div>

      <div className="pt-6 border-t-4 border-black">
        <h3 className="font-black text-xl mb-4">SOCIAL MEDIA LINKS</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-black uppercase mb-2 flex items-center gap-2">
              <Instagram size={16} /> Instagram
            </label>
            <input
              type="text"
              value={settings.social?.instagram || ''}
              onChange={(e) => updateSocial('instagram', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-mono"
              placeholder="https://instagram.com/owlfit"
            />
          </div>

          <div>
            <label className="block text-sm font-black uppercase mb-2 flex items-center gap-2">
              <Facebook size={16} /> Facebook
            </label>
            <input
              type="text"
              value={settings.social?.facebook || ''}
              onChange={(e) => updateSocial('facebook', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-mono"
              placeholder="https://facebook.com/owlfit"
            />
          </div>

          <div>
            <label className="block text-sm font-black uppercase mb-2 flex items-center gap-2">
              <Twitter size={16} /> Twitter
            </label>
            <input
              type="text"
              value={settings.social?.twitter || ''}
              onChange={(e) => updateSocial('twitter', e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-mono"
              placeholder="https://twitter.com/owlfit"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
